import { useMarket } from "@app/_context";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import type { z } from "zod";

import { isAdyenCheckoutEnabled } from "@/config/checkout";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import {
  useMergedAddressSchema,
  useUpdateCheckoutBuyer,
  useUpdateCheckoutReciver,
} from "./address/Address.hooks";
import {
  getAddressSchema,
  getDeliveryAddressSchema,
  getZipCodeOnlySchema,
} from "./address/Address.utils";
import CheckoutAddressFields from "./address/CheckoutAddressFields";
import CheckoutDeliveryAddressFields from "./address/CheckoutDeliveryAddressFields";
import { useCheckout, useCheckoutContext } from "./Checkout.hooks";
import { checkoutStore } from "./Checkout.store";
import { getCheckoutLocalStorage } from "./Checkout.utils";
import CheckoutContinueButton from "./CheckoutContinueButton";
import CheckoutMinimumOrderNotification from "./CheckoutMinimumOrderNotification";

export default function CheckoutAddressForm() {
  const { data } = useCheckout();
  const marketConfig = useMarket().state.market;

  // Hooks
  const { dictionary, deliveryStepRef } = useCheckoutContext();
  const addressSchemaResolver = useMergedAddressSchema({
    dictionary,
    marketConfig,
  });

  // Utils
  const cartCookie = getCartCookie();
  const checkoutLocalStorage = getCheckoutLocalStorage();
  const zipCodeOnlySchema = getZipCodeOnlySchema({
    marketCode: marketConfig.code,
    dictionary,
  });
  const addressSchema = getAddressSchema({
    marketCode: marketConfig.code,
    dictionary,
  });
  const deliveryAddressSchema = getDeliveryAddressSchema({
    marketCode: marketConfig.code,
    dictionary,
  });

  // Mutations
  const { mutate: updateCheckoutBuyerMutation } = useUpdateCheckoutBuyer();
  const { mutate: updateCheckoutReciverMutation } = useUpdateCheckoutReciver();

  // Stores
  const address = checkoutStore((state) => state.address);
  const ableToComplete = checkoutStore((state) => state.ableToComplete);
  const hasSeparateDeliveryAddress = checkoutStore(
    (state) => state.hasSeparateDeliveryAddress,
  );

  // Actions
  const setAddress = checkoutStore((state) => state.setAddress);
  const setDeliveryAddress = checkoutStore((state) => state.setDeliveryAddress);

  /**
   * Workaround to scroll to the delivery when the delivery options has rendered.
   * NShift does not have a good way to handle this.
   *
   * This is to prevent the page from scrolling to the delivery step when the page is loaded.
   * We only want to scroll when zipCode is set and the user clicks on the continue button.
   */
  useEffect(() => {
    // If zipCode is already set, we don't need to scroll to the delivery step.
    if (address.zipCode) {
      return;
    }

    checkoutStore.subscribe(
      (state) => state.canScrollToDelivery,
      (canScrollToDelivery) => {
        if (canScrollToDelivery) {
          deliveryStepRef?.current?.scrollIntoView({
            block: "start",
            behavior: "smooth",
          });
        }
      },
    );
  }, [deliveryStepRef, address.zipCode]);

  const methods = useForm<
    z.input<typeof addressSchema>,
    any,
    z.output<typeof addressSchema>
  >({
    // @ts-expect-error expected
    resolver: zodResolver(addressSchemaResolver),
    // Update the form field values with the persisted data from the store.
    defaultValues: {
      ...checkoutLocalStorage?.state?.address,
      ...checkoutLocalStorage?.state?.deliveryAddress,
      /**
       * Don't remove this unless you have a good reason to do so.
       * Workaround to set the country value manually in the form since the field name does not register properly in CheckoutCountrySelect.tsx
       * We may have to do something like this when integrating with UI libraries.
       * @see https://react-hook-form.com/get-started#IntegratingwithUIlibraries
       */
      country: marketConfig.code,
      // @ts-expect-error expected
      deliveryCountry: marketConfig.code,
    },
  });

  const onSubmit = methods.handleSubmit((data) => {
    if (isAdyenCheckoutEnabled({ marketConfig }) === false) {
      const zipCodeData = zipCodeOnlySchema.parse(data);

      setAddress(zipCodeData).then(() => {
        deliveryStepRef?.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      });

      return;
    }

    const addressData = addressSchema.parse(data);

    setAddress(addressData).then(() => {
      updateCheckoutBuyerMutation({
        cartId: cartCookie?.value ?? "",
        locale: marketConfig.locale ?? "",
        buyer: {
          firstName: addressData.firstName,
          lastName: addressData.lastName,
          email: addressData.email,
          cellPhone: addressData.phoneNumber,
          companyName: addressData.company,
          address: {
            line1: addressData.line1,
            line2: addressData.line2,
            zip: addressData.zipCode,
            city: addressData.city,
            country: addressData.country ?? marketConfig.code,
          },
        },
      });
    });

    if (hasSeparateDeliveryAddress) {
      const deliveryAddressData = deliveryAddressSchema.parse(data);

      setDeliveryAddress(deliveryAddressData).then(() => {
        updateCheckoutReciverMutation({
          cartId: cartCookie?.value ?? "",
          locale: marketConfig.locale ?? "",
          receiver: {
            email: addressData.email,
            firstName: deliveryAddressData.deliveryFirstName,
            lastName: deliveryAddressData.deliveryLastName,
            cellPhone: addressData.phoneNumber,
            address: {
              line1: deliveryAddressData.deliveryLine1,
              zip: deliveryAddressData.deliveryZipCode,
              city: deliveryAddressData.deliveryCity,
              country: deliveryAddressData.deliveryCountry ?? marketConfig.code,
            },
          },
        });
      });
    }

    deliveryStepRef?.current?.scrollIntoView({
      block: "start",
      behavior: "smooth",
    });
  });

  if (!data?.items.length) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <form
        className="grid grid-cols-2 gap-2 px-5 py-6 lg:px-16 lg:py-10"
        onSubmit={onSubmit}
      >
        <CheckoutAddressFields />
        <CheckoutDeliveryAddressFields />
        <CheckoutContinueButton
          disabled={!ableToComplete}
          className="col-span-2"
          type="submit"
        >
          {dictionary.goToDelivery}
        </CheckoutContinueButton>
        <CheckoutMinimumOrderNotification className="col-span-2" />
      </form>
    </FormProvider>
  );
}
